import { useState } from 'react';
import { deleteMatch } from '../../app/homeB';
import type { HomeBMatch } from '../../app/homeB';
import { log } from '../../app/logger';
import type { DeleteEventB } from '../../domain/homeB';
import symbolLight from '../brand/symbol.svg?raw';
import symbolDark from '../brand/symbol-white.svg?raw';
import { useHomeB } from './useHomeB';

/**
 * B spec §2.4 — **the Matches tab**, the out-of-match shell's home (build unit B-5).
 *
 * Top to bottom, exactly the spec's order: the In-progress card (any resumable match — one tap
 * into the takeover), the Needs-finishing queue (§2.2's definition under F-1's approved
 * ruling), the New-match primary action, then match history (`byMatchHistory` order; each row:
 * opponent, date, format chip, purpose chip, state). An empty database renders the brand
 * empty-state with New match and Import-an-archive instead.
 *
 * The screen knows nothing about what is on the other side of a tap (the `HomeScreen`
 * discipline): it reads, renders, and reports. The shell decides what opening a match means —
 * which is what lets B-7 (setup), B-8 (takeover) and B-18 (match record) wire their surfaces
 * in without touching this file.
 *
 * ── Copy does requirement work ───────────────────────────────────────────────
 *
 * The queue's note says there is no hurry: like A's pending-distillation queue it is a list on
 * a screen the user is already on — never a badge, a count on the tab, or a prompt.
 *
 * ── The armed delete (Andrew's ruling, 2026-08-19) ───────────────────────────
 *
 * *"There should be a way to delete matches from the match history list… A delete button with
 * 'are you sure?' confirmation should suffice."* — scoped in the same breath to *"abandoned and
 * never-started matches only"*.
 *
 * **Both populations the ruling names are reachable on this screen, and they are in different
 * groups.** An abandoned match is `fullTime` with the flag and renders in **match history**; a
 * never-started one is `preMatch` and renders on the **In-progress card** — `isMatchHistoryB`
 * excludes `preMatch` by name, so it is not in the history list at all. Andrew met the second one
 * in the field on 2026-08-19: a match whose setup he never finished, sitting on this page reading
 * *In progress · Setup*, with nothing anywhere that could remove it. B had no discard control on
 * any surface; A's Pre-Match screen has had one since U-4. So the same armed control is rendered
 * for both, with the copy branching on `deleteEvent` and the engine work branching on nothing —
 * `DiscardSetup` already existed.
 *
 * The confirmation is B's **armed** pattern (`myteam/PlayerSheet.tsx`, unit B-28): press to arm,
 * press again to confirm, with a *Keep* out. Not a modal and not `window.confirm` — on a phone
 * held one-handed a dialog moves the decision away from the thing being decided about, and
 * consistency with the one destructive control B already has is worth more than novelty here.
 *
 * **What disarms it, decided rather than inherited:**
 *
 * | Event | Disarms | Why |
 * |---|---|---|
 * | Arming another row or card | yes, by construction | one `armed` match id across both groups, so two controls cannot be armed |
 * | Leaving the tab, or any pushed surface | yes | `TabShellB` unmounts this screen, taking the state |
 * | *Keep* | yes | the explicit way out |
 * | Any dispatch outcome — done, refused, or faulted | yes | an armed id could name a gone row, and a live destructive control beside a fault message is a bad pair |
 * | Scrolling | **no** | scrolling is how the list is read; disarming on it makes a two-press control unusable one-handed |
 * | A timeout | **no** | nothing here is time-critical, and a destructive control that vanishes mid-reach is a worse failure than one that stays armed. `PlayerSheet` has none either |
 *
 * The affordance is offered on **history rows and the In-progress card**, never on the
 * Needs-finishing rendering of a match that also stands in history: the queue is a call-to-action
 * about finishing, and one match must reach exactly one armed control.
 *
 * **The control is a courtesy, not the guarantee.** `machine.transition` refuses `DeleteMatch` on a
 * match that is not abandoned, and refuses either event from a state its table has no row for,
 * whatever this screen renders — so a stale row or card is harmless.
 *
 * ── Where the In-progress card's tap goes (the separate unit, 2026-08-19) ────
 *
 * The delete unit above left every tap handler byte-for-byte alone and named the rest as a second
 * defect — *"tapping an unfinished setup lands somewhere wrong and offers no route back to setup."*
 * This is that second unit, and the note it left is now spent: the card's tap **branches**.
 *
 * > *"When I tap the match, it takes me to the match in progress page, from which I cannot escape
 * > without abandoning the match."*
 *
 * The card carries `openTarget` (`domain/homeB.openTargetForB`), so this screen keeps the
 * `HomeScreen` discipline it was built on — it does not ask what state the match is in, it reads
 * the navigation the domain already decided and reports it. `onOpenRecord` and every queue/history
 * row are untouched: they were never wrong.
 */

export interface MatchesHomeProps {
  /** §2.4 — the New-match primary action (routes to match setup, B-7's surface). */
  readonly onNewMatch: () => void;
  /** The In-progress card's tap on a match **under way** — into the takeover shell (B-8's surface). */
  readonly onOpenMatch: (matchId: string) => void;
  /**
   * The In-progress card's tap on an **unstarted setup** — back into match setup (B-7's surface),
   * which is where `AppB`'s launch resolve has sent the same match since B-7.
   *
   * Takes no match id, and that is the setup surface's shape rather than an omission here:
   * `SetupScreen` edits *the* unstarted setup (`loadSetupContext` reads
   * `listMatchesByState([preMatch])[0]`, and `machine.transition`'s `anotherPreMatchExists` guard
   * is what makes "the" singular), so `TabShellB`'s `setup` overlay carries no id either and this
   * signature matches the thing it opens. A second setup row would be a data fault upstream of
   * this screen, not a decision it could make better.
   */
  readonly onOpenSetup: () => void;
  /** A queue or history row's tap — the match record (B-18's surface). */
  readonly onOpenRecord: (matchId: string) => void;
  /** Empty-state secondary action — §2.4: *"Import an archive (routes to More → Import)"*. */
  readonly onImportArchive: () => void;
}

const MODULE = 'ui-b/matches';

/**
 * The armed control's three strings, per deleting transition — **the only thing that branches**.
 *
 * The two populations are destroyed by the same cascade and confirmed by the same control, so the
 * branch is copy and nothing else. It is a total `Record` over {@link DeleteEventB} rather than a
 * ternary, so a third deleting transition would be a compile error here rather than a control that
 * silently renders an abandoned match's wording over an unstarted one's.
 *
 * The setup arm says *"anything set up for it"* and deliberately **not** *"nothing is lost"*: a
 * `preMatch` match can already hold a locked board, because PRE-7's exits assign positions while
 * the state is still `preMatch` (§6.0) and only Kickoff leaves it. A confirmation that promised
 * nothing was there would be false on exactly the setup that took the longest to build.
 */
const COPY: Readonly<Record<DeleteEventB, { arm: string; confirm: string; question: (subject: string) => string }>> = {
  DeleteMatch: {
    arm: 'Delete…',
    confirm: 'Delete for good',
    question: (subject) => `Delete ${subject}? Everything captured in it goes with it.`,
  },
  DiscardSetup: {
    arm: 'Discard setup…',
    confirm: 'Discard for good',
    question: (subject) => `Discard the setup for ${subject}? Anything set up for it goes with it.`,
  },
};

export function MatchesHome({
  onNewMatch,
  onOpenMatch,
  onOpenSetup,
  onOpenRecord,
  onImportArchive,
}: MatchesHomeProps) {
  const { state, actions } = useHomeB();
  /** The one armed row, by match id. One value, so two rows can never be armed at once. */
  const [armed, setArmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A refusal sentence from the engine (`DispatchResult.message`). Never a fatal banner. */
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * `MyTeamScreen`'s one write path, in shape: dispatch → sentence on a refusal → re-read on both
   * resolved arms. **A refusal gets the re-read too**, because a refusal usually means the screen
   * was stale, and re-reading is what actually repairs that — the row whose control the user could
   * not act on is gone before they can press it again. A thrown fault is the third arm and is
   * treated differently; see the catch.
   */
  const remove = (matchId: string, via: DeleteEventB): void => {
    setBusy(true);
    setNotice(null);
    void deleteMatch(matchId, via)
      .then((result) => {
        if (!result.ok) setNotice(result.message);
        setArmed(null);
        actions.refresh();
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.4: a match delete failed', {
          matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setNotice('That match could not be deleted. The log has the detail.');
        // Disarmed, but NOT re-read. A live destructive control beside a fault message is a bad
        // pair, so the arm is dropped; the write failing is no evidence the read is broken, and a
        // refresh that also failed would take the whole surface to its unreadable state.
        setArmed(null);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (state.phase === 'loading')
    return <p className="homeb-empty">Reading your matches…</p>;
  if (state.phase === 'failed' || state.view === null)
    return (
      <p className="homeb-empty" role="alert">
        Your matches could not be read. The log has the detail.
      </p>
    );

  const view = state.view;
  const empty =
    view.inProgress.length === 0 && view.history.length === 0;

  /**
   * The armed delete, rendered identically wherever it is offered.
   *
   * One implementation for the history row and the In-progress card, not two: both are the same
   * gesture on the same decision, and a second copy is how the two populations' confirmations come
   * to disagree about what a press destroys. The **copy** branches on `deleteEvent` and nothing
   * else does — see `COPY` below.
   */
  const armedDelete = (item: HomeBMatch) => {
    // Captured rather than read through `item` inside the handler: a property narrowing does not
    // survive into a closure, and the alternative is a non-null assertion on the value that
    // decides which destructive transition is dispatched.
    const via = item.deleteEvent;
    if (via === null) return null;
    const copy = COPY[via];
    const subject = `${item.opponent}, ${item.date}`;
    return (
      <div className="homeb-row__danger">
        {armed === item.matchId ? (
          <>
            {/* Names the match, `PlayerSheet`'s armed line's rule ("Remove #7 Maya?"): every
                control on this screen reads identically, so a confirmation that said only "this
                match" would be a destructive press with no way to check it is the right one. */}
            <p className="homeb-row__warn" data-testid={`homeb-delete-warn-${item.matchId}`}>
              {copy.question(subject)}
            </p>
            <button
              type="button"
              className="homeb-row__confirm"
              data-testid={`homeb-delete-confirm-${item.matchId}`}
              aria-label={`${copy.confirm}: ${subject}`}
              disabled={busy}
              onClick={() => {
                remove(item.matchId, via);
              }}
            >
              {copy.confirm}
            </button>
            <button
              type="button"
              className="homeb-row__keep"
              data-testid={`homeb-delete-keep-${item.matchId}`}
              aria-label={`Keep ${subject}`}
              onClick={() => {
                setArmed(null);
              }}
            >
              Keep
            </button>
          </>
        ) : (
          <button
            type="button"
            className="homeb-row__delete"
            data-testid={`homeb-delete-${item.matchId}`}
            aria-label={`${copy.arm} ${subject}`}
            disabled={busy}
            onClick={() => {
              setNotice(null);
              setArmed(item.matchId);
            }}
          >
            {copy.arm}
          </button>
        )}
      </div>
    );
  };

  /** §2.4's row anatomy, shared by the queue and history (the queue is a subset rendered as a
   *  call-to-action; membership lives in `domain/homeB.ts`, never re-derived here).
   *
   *  @param offerDelete history only — see the armed-delete note in this file's header. */
  const row = (item: HomeBMatch, testId: string, offerDelete = false) => (
    <li
      key={item.matchId}
      className="homeb-row"
      data-abandoned={item.abandoned ? 'true' : 'false'}
      data-deletable={offerDelete && item.deleteEvent !== null ? 'true' : 'false'}
    >
      <button
        type="button"
        className="homeb-row__open"
        data-testid={`${testId}-${item.matchId}`}
        onClick={() => {
          onOpenRecord(item.matchId);
        }}
      >
        <span className="homeb-row__head">
          <span className="homeb-row__opponent">{item.opponent}</span>
          <span className="homeb-row__date">{item.date}</span>
        </span>
        <span className="homeb-row__meta">
          {item.format !== null && <span className="homeb-chip">{item.format}</span>}
          {item.purposeLabel !== null && <span className="homeb-chip">{item.purposeLabel}</span>}
          <span className="homeb-row__state">{item.stateLabel}</span>
        </span>
      </button>
      {/* Stacked UNDER the row rather than beside it: a control added to the right of the open
          button would put new intrinsic-width content in a grid row at a 390 pt viewport, which is
          this project's twice-shipped defect. Nothing here competes for horizontal space. */}
      {offerDelete && armedDelete(item)}
    </li>
  );

  return (
    <div
      className="homeb"
      data-testid="homeb"
      data-read={String(state.reads)}
      data-inprogress={String(view.inProgress.length)}
      data-needsfinishing={String(view.needsFinishing.length)}
      data-history={String(view.history.length)}
    >
      <h1 className="homeb__title">Matches</h1>

      {/* An engine refusal is an ordinary event here, not a fault (`MyTeamScreen`'s rule): the
          message the domain already wrote is the sentence the user should read, so it renders in a
          quiet status line and never in the fatal banner. */}
      {notice !== null && (
        <p className="homeb-notice" data-testid="homeb-notice" role="status">
          {notice}
          <button
            type="button"
            className="homeb-notice__dismiss"
            data-testid="homeb-notice-dismiss"
            onClick={() => {
              setNotice(null);
            }}
          >
            Dismiss
          </button>
        </p>
      )}

      {/* The card is wrapped so the armed delete can stack under it (the delete unit's addition,
          untouched here). `data-opens` renders the branch the tap will take, so a verification
          pass can read the DECISION off the card as well as observe where the press lands — the
          two are different claims and this project has shipped a defect in the gap between them. */}
      {view.inProgress.map((item) => (
        <div
          key={item.matchId}
          className="homeb-live-group"
          data-deletable={item.deleteEvent !== null ? 'true' : 'false'}
        >
          <button
            type="button"
            className="homeb-live"
            data-testid={`homeb-inprogress-${item.matchId}`}
            data-opens={item.openTarget ?? 'takeover'}
            onClick={() => {
              // The domain decided this (`openTargetForB`); the screen only obeys it. `null` cannot
              // reach here — this group's membership IS `openTarget !== null`, asserted against
              // `isResumable` over every state in `domain/homeB.test.ts` — and the takeover is the
              // right landing if damaged data ever made it: `takeoverSurfaceFor` answers `left` for
              // an unresumable row and the chrome's hand-off lands on that match's record, so the
              // fallback is a screen about the match rather than a trap.
              if (item.openTarget === 'setup') onOpenSetup();
              else onOpenMatch(item.matchId);
            }}
          >
            <span className="homeb-live__label">In progress</span>
            <span className="homeb-live__opponent">{item.opponent}</span>
            <span className="homeb-live__meta">
              {item.stateLabel} · {item.date}
            </span>
          </button>
          {/* A match under way is `deleteEvent: null` — SES-6 says a live match is abandoned
              first — so this renders for the unstarted setup and for nothing else here. */}
          {armedDelete(item)}
        </div>
      ))}

      {view.needsFinishing.length > 0 && (
        <section className="homeb-section" data-testid="homeb-needsfinishing">
          <h2 className="homeb-section__title">Needs finishing</h2>
          <p className="homeb-section__note">
            No hurry — cards and flags wait as long as you like.
          </p>
          <ul className="homeb-list">
            {view.needsFinishing.map((item) => row(item, 'homeb-finish'))}
          </ul>
        </section>
      )}

      {!empty && (
        <button
          type="button"
          className="homeb-new"
          data-testid="homeb-new-match"
          onClick={onNewMatch}
        >
          New match
        </button>
      )}

      {view.history.length > 0 && (
        <section className="homeb-section" data-testid="homeb-history">
          <h2 className="homeb-section__title">Match history</h2>
          <ul className="homeb-list">
            {view.history.map((item) => row(item, 'homeb-history', true))}
          </ul>
        </section>
      )}

      {empty && (
        <div className="homeb-first" data-testid="homeb-first-run">
          <div className="homeb-first__mark" aria-hidden="true">
            <span className="only-light" dangerouslySetInnerHTML={{ __html: symbolLight }} />
            <span className="only-dark" dangerouslySetInnerHTML={{ __html: symbolDark }} />
          </div>
          <h2 className="homeb-first__title">No matches yet</h2>
          <p className="homeb-first__note">
            Set up your first match, or bring your data across from an archive.
          </p>
          <button
            type="button"
            className="homeb-new"
            data-testid="homeb-new-match"
            onClick={onNewMatch}
          >
            New match
          </button>
          <button
            type="button"
            className="homeb-import"
            data-testid="homeb-import-archive"
            onClick={onImportArchive}
          >
            Import an archive
          </button>
        </div>
      )}
    </div>
  );
}
